import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import { DateRangePicker } from "./DateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ja } from "date-fns/locale";

// 型は親から渡す
interface MissionFormBaseProps<T> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  onValidationChange: (isValid: boolean) => void;
  radioDisabled?: boolean;
}

export const MissionFormBase = <
  T extends {
    missionTypeId: string;
    title: string;
    conditions: string;
    point: number;
    startedAt: string;
    endedAt: string;
    missionDisplay: { missionDisplayTypeId: string; detail: string };
  },
>({
  formData,
  setFormData,
  onValidationChange,
  radioDisabled = false,
}: MissionFormBaseProps<T>) => {
  // 共通ステート
  const [formErrors, setFormErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({ title: false });

  // formData.titleが変更されたときに、detail内のtitleも同期
  useEffect(() => {
    try {
      const detailObj = JSON.parse(formData.missionDisplay.detail);
      if (detailObj.title !== formData.title) {
        const updatedDetail = {
          ...detailObj,
          title: formData.title,
        };
        setFormData((prev: any) => ({
          ...prev,
          missionDisplay: {
            ...prev.missionDisplay,
            detail: JSON.stringify(updatedDetail),
          },
        }));
      }
    } catch (e) {
      console.error("JSON parse error:", e);
    }
  }, [formData.title]);

  // 日付変換
  function formatDateToCustomString(date: Date | null): string {
    if (!date) return "";
    const pad = (num: number, size: number) => {
      let s = String(num);
      while (s.length < size) s = "0" + s;
      return s;
    };
    const yyyy = date.getFullYear();
    const MM = pad(date.getMonth() + 1, 2);
    const dd = pad(date.getDate(), 2);
    const HH = pad(date.getHours(), 2);
    const mm = pad(date.getMinutes(), 2);
    const ss = pad(date.getSeconds(), 2);
    const SSS = pad(date.getMilliseconds(), 3);
    const SSSSSS = SSS + "000";
    return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}.${SSSSSS}`;
  }

  function formatYearMonth(date: Date | null): string {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}/${month}`;
  }

  // 日付範囲変更時のハンドラ（元のロジックに倣う）
  const handleDateChange = (start: Date | null, end: Date | null) => {
    let startedAt = "";
    let endedAt: string | null = null;
    if (start) {
      startedAt = formatDateToCustomString(start);
    }
    if (end) {
      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);
      endedAt = formatDateToCustomString(endOfDay);
    } else {
      endedAt = null;
    }
    setFormData((prev: any) => ({
      ...prev,
      startedAt,
      endedAt,
    }));
    setTouched((prev: any) => ({ ...prev, startDate: true, endDate: true }));
    validateForm();
  };

  // バリデーション
  const validateForm = () => {
    const conditions = JSON.parse(formData.conditions);
    const detailObj = JSON.parse(formData.missionDisplay.detail);
    const errors: any = {};
    // title: 空文字列・null・undefinedはNG
    if (!formData.title || formData.title.trim() === "") {
      errors.title = "必須項目です";
    } else if (formData.title.length > 128) {
      errors.title = "128字以内で入力してください";
    }
    // startedAt/endedAt: 空文字列・null・undefinedはNG
    if (!formData.startedAt || formData.startedAt.trim() === "") {
      errors.startDate = "開始日を入力してください";
    } else if (formData.endedAt && formData.startedAt > formData.endedAt) {
      errors.startDate = "開始日＜終了日としてください";
      errors.endDate = "開始日＜終了日としてください";
    }
    // missionTypeIdごとの条件
    if (formData.missionTypeId === "019372c6-c935-7261-8955-f55c1b7d9ff2") {
      if (!conditions.targetYearMonth || conditions.targetYearMonth === "") {
        errors.targetYearMonth = "対象月を入力してください";
      }
    }
    if (formData.missionTypeId === "019372c6-c935-737e-b37c-c34141af61be") {
      if (!conditions.targetYearMonth || conditions.targetYearMonth === "") {
        errors.targetYearMonth = "対象月を入力してください";
      }
      if (
        conditions.threshold === null ||
        conditions.threshold === undefined ||
        conditions.threshold === ""
      ) {
        errors.threshold = "積算充電量を入力してください";
      }
      if (
        typeof conditions.threshold === "number" &&
        conditions.threshold < 0
      ) {
        errors.threshold = "0以上の数値を入力してください";
      }
    }
    // URLバリデーション
    const isValidURL = (url: string): boolean => {
      try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch (_) {
        return false;
      }
    };
    if (detailObj.url && detailObj.url !== "" && !isValidURL(detailObj.url)) {
      errors.url = "有効なURLを入力してください";
    }
    // ポイント: null, undefined, 空文字列はNG
    if (formData.point === null || formData.point === undefined) {
      errors.point = "ポイント数を入力してください";
    }
    if (typeof formData.point === "number" && formData.point < 0) {
      errors.point = "0以上の数値を入力してください";
    }
    setFormErrors(errors);
    onValidationChange(Object.keys(errors).length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [formData]);

  // ハンドラ
  const handleChange = (field: string) => (e: any) => {
    const value = e.target.value;
    let parsedValue = value;
    if (field === "point") {
      parsedValue = value === "" ? "" : Number(value);
    }
    setFormData((prev: any) => ({
      ...prev,
      [field]: parsedValue,
    }));
  };

  const handleChangeJsonField =
    (jsonKey: string, field: string) => (eOrValue: any) => {
      const value =
        typeof eOrValue === "object" && eOrValue?.target
          ? eOrValue.target.value
          : eOrValue;
      let parsedValue = value;
      if (field === "threshold") {
        parsedValue = value === "" ? "" : Number(value);
      }
      if (field === "description") {
        parsedValue = value.split("\n");
      }
      try {
        const currentJson =
          jsonKey === "detail"
            ? JSON.parse(formData.missionDisplay.detail)
            : JSON.parse(formData.conditions);
        const updatedJson = { ...currentJson };
        // urlまたはimageが空の場合はプロパティを削除、それ以外は設定
        if (
          (field === "url" || field === "image") &&
          (!parsedValue || parsedValue === "")
        ) {
          delete updatedJson[field];
        } else {
          updatedJson[field] = parsedValue;
        }
        if (jsonKey === "detail") {
          setFormData((prev: any) => ({
            ...prev,
            missionDisplay: {
              ...prev.missionDisplay,
              detail: JSON.stringify(updatedJson),
            },
          }));
        } else {
          setFormData((prev: any) => ({
            ...prev,
            conditions: JSON.stringify(updatedJson),
          }));
        }
      } catch (e) {
        console.error("JSON parse error:", e);
      }
    };

  // missionTypeId専用ハンドラ（元々の関数を使用）
  const handleChangeMissionType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log(formData);
    setFormData((prev: any) => ({
      ...prev,
      missionTypeId: event.target.value,
    }));
  };

  // missionDisplayTypeId専用ハンドラ
  const handleChangeDisplayType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTypeId = event.target.value;
    setFormData((prev: any) => ({
      ...prev,
      missionDisplay: {
        ...prev.missionDisplay,
        missionDisplayTypeId: newTypeId,
        detail: prev.missionDisplay.detail,
      },
    }));
  };

  // UI
  return (
    <div>
      <Box sx={{ width: "100%", height: "100%" }}>
        <Box sx={{ width: "100%", margin: "0 auto" }}>
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: 5,
              backgroundColor: "#f9f9f9",
              boxShadow: 1,
              width: "100%",
              margin: "0px auto",
            }}
          >
            {/* タイトル */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              margin="20px auto"
              paddingBottom="100px"
              sx={{ maxHeight: 100 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                ミッションタイトル
              </Typography>
              <TextField
                required
                id="outlined-required"
                label=""
                value={formData.title}
                onChange={handleChange("title")}
                onBlur={() => {
                  setTouched({ title: true });
                  validateForm();
                }}
                error={touched.title && !!formErrors.title}
                helperText={touched.title ? formErrors.title : ""}
              />
            </Box>
            <Divider
              sx={{
                width: "680px",
                borderColor: "var(--color-bold-divider)",
                borderBottomWidth: 2,
              }}
            />
            {/* 配信期間 */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              margin="20px auto"
              paddingBottom="100px"
              sx={{ maxHeight: 100 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                配信期間
              </Typography>
              <DateRangePicker
                startDate={
                  formData.startedAt ? new Date(formData.startedAt) : null
                }
                endDate={formData.endedAt ? new Date(formData.endedAt) : null}
                onStartChange={(date) => {
                  // まずstartedAtのみ更新
                  const formatted = formatDateToCustomString(date);
                  setFormData((prev: any) => ({
                    ...prev,
                    startedAt: formatted,
                  }));
                  // 両方揃っていればhandleDateChangeでendOfDay処理
                  if (date && formData.endedAt) {
                    const end = new Date(formData.endedAt);
                    handleDateChange(date, end);
                  }
                  setTouched((prev: any) => ({
                    ...prev,
                    startDate: true,
                    endDate: true,
                  }));
                  validateForm();
                }}
                onEndChange={(date) => {
                  // endedAtが未入力(null)ならnullをセット
                  let formatted: string | null = null;
                  if (date) {
                    formatted = formatDateToCustomString(date);
                  }
                  setFormData((prev: any) => ({
                    ...prev,
                    endedAt: formatted,
                  }));
                  // 両方揃っていればhandleDateChangeでendOfDay処理
                  if (date && formData.startedAt) {
                    const start = new Date(formData.startedAt);
                    handleDateChange(start, date);
                  }
                  if (!date) {
                    setFormData((prev: any) => ({
                      ...prev,
                      endedAt: null,
                    }));
                  }
                  setTouched((prev: any) => ({
                    ...prev,
                    startDate: true,
                    endDate: true,
                  }));
                  validateForm();
                }}
                startError={touched.startDate && !!formErrors.startDate}
                endError={touched.endDate && !!formErrors.endDate}
                startHelperText={touched.startDate ? formErrors.startDate : ""}
                endHelperText={touched.endDate ? formErrors.endDate : ""}
              />
            </Box>
            <Divider
              sx={{
                width: "680px",
                borderColor: "var(--color-bold-divider)",
                borderBottomWidth: 2,
              }}
            />
            {/* ミッションタイプ */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              margin="20px auto"
            >
              <FormControl>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ textAlign: "left" }}
                >
                  ミッションタイプ
                </Typography>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={formData.missionTypeId}
                  defaultValue={"019372c6-c935-7261-8955-f55c1b7d9ff2"}
                  onChange={handleChangeMissionType}
                  sx={{ ml: 2 }}
                >
                  <FormControlLabel
                    value="019372c6-c935-7261-8955-f55c1b7d9ff2"
                    control={<Radio />}
                    label="アプリ起動"
                    disabled={radioDisabled}
                  />
                  <FormControlLabel
                    value="019372c6-c935-732d-b4cc-889b56cd7857"
                    control={<Radio />}
                    label="電気代シミュレーション"
                    disabled={radioDisabled}
                  />
                  <FormControlLabel
                    value="019372c6-c935-737e-b37c-c34141af61be"
                    control={<Radio />}
                    label="積算充電量"
                    disabled={radioDisabled}
                  />
                  <FormControlLabel
                    value="019372c6-c935-72c6-ab12-e352aba76fac"
                    control={<Radio />}
                    label="記事閲覧"
                    disabled={radioDisabled}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            {/* アプリ起動 */}
            {formData.missionTypeId ===
              "019372c6-c935-7261-8955-f55c1b7d9ff2" && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                margin="20px auto"
                paddingBottom="100px"
                sx={{ maxHeight: 100 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  　ミッションの対象月
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                >
                  <DatePicker
                    label={"対象月"}
                    value={
                      JSON.parse(formData.conditions).targetYearMonth
                        ? new Date(
                            JSON.parse(formData.conditions).targetYearMonth +
                              "/01"
                          )
                        : null
                    }
                    views={["year", "month"]}
                    format={"yyyy/MM"}
                    onChange={(date) => {
                      // アプリ起動はyyyy/MMで格納
                      const formatted = formatYearMonth(date);
                      console.log("Formatted value:", formatted); // デバッグ用

                      // handleChangeJsonFieldを使わずに直接更新
                      try {
                        const currentConditions = JSON.parse(
                          formData.conditions
                        );
                        const updatedConditions = {
                          ...currentConditions,
                          targetYearMonth: formatted,
                        };
                        setFormData((prev: any) => ({
                          ...prev,
                          conditions: JSON.stringify(updatedConditions),
                        }));
                        console.log(
                          "Updated conditions:",
                          JSON.stringify(updatedConditions)
                        ); // デバッグ用
                      } catch (e) {
                        console.error("JSON parse error:", e);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: 150 },
                        onBlur: (event: any) => {
                          setTouched((prev: any) => ({
                            ...prev,
                            targetYearMonth: true,
                          }));
                          validateForm();
                        },
                        error:
                          touched.targetYearMonth &&
                          !!formErrors.targetYearMonth,
                        helperText: touched.targetYearMonth
                          ? formErrors.targetYearMonth
                          : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            )}
            {/* 積算充電量 */}
            {formData.missionTypeId ===
              "019372c6-c935-737e-b37c-c34141af61be" && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  margin="20px auto"
                  paddingBottom="100px"
                  sx={{ maxHeight: 100 }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    　ミッションの対象月
                  </Typography>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ja}
                  >
                    <DatePicker
                      label={"対象月"}
                      value={
                        JSON.parse(formData.conditions).targetYearMonth
                          ? new Date(
                              JSON.parse(formData.conditions).targetYearMonth +
                                "/01"
                            )
                          : null
                      }
                      views={["year", "month"]}
                      format={"yyyy/MM"}
                      onChange={(date) => {
                        // 積算充電量も "yyyy/MM" 形式で格納
                        const formatted = formatYearMonth(date);
                        handleChangeJsonField(
                          "conditions",
                          "targetYearMonth"
                        )(formatted);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { width: 150 },
                          onBlur: (event: any) => {
                            setTouched((prev: any) => ({
                              ...prev,
                              targetYearMonth: true,
                            }));
                            validateForm();
                          },
                          error:
                            touched.targetYearMonth &&
                            !!formErrors.targetYearMonth,
                          helperText: touched.targetYearMonth
                            ? formErrors.targetYearMonth
                            : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  margin="20px auto"
                  paddingBottom="100px"
                  sx={{ maxHeight: 100 }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    　積算充電量(kWh)
                  </Typography>
                  <TextField
                    required
                    id="outlined-required"
                    label=""
                    type="number"
                    value={JSON.parse(formData.conditions).threshold ?? ""}
                    onChange={handleChangeJsonField("conditions", "threshold")}
                    onBlur={() => {
                      setTouched({ threshold: true });
                      validateForm();
                    }}
                    error={touched.threshold && !!formErrors.threshold}
                    helperText={touched.threshold ? formErrors.threshold : ""}
                  />
                </Box>
              </>
            )}
            <Divider
              sx={{
                width: "680px",
                borderColor: "var(--color-bold-divider)",
                borderBottomWidth: 2,
              }}
            />
            {/* ミッション表示タイプ */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              margin="20px auto"
            >
              <FormControl>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ textAlign: "left" }}
                >
                  ミッション表示タイプ
                </Typography>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={formData.missionDisplay.missionDisplayTypeId}
                  onChange={handleChangeDisplayType}
                  sx={{ ml: 2 }}
                >
                  <FormControlLabel
                    value="019372c6-c935-77cd-aea1-e2749cb54862"
                    control={<Radio />}
                    label="ネイティブ"
                    disabled={radioDisabled}
                  />
                  <FormControlLabel
                    value="019372c6-c935-7000-9eef-4043e704ad85"
                    control={<Radio />}
                    label="WebView"
                    disabled={radioDisabled}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            {/* ネイティブ */}
            {formData.missionDisplay.missionDisplayTypeId ===
              "019372c6-c935-77cd-aea1-e2749cb54862" && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  margin="20px auto"
                  paddingBottom="100px"
                  sx={{ maxHeight: 100 }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    外部サイトのURL
                  </Typography>
                  <form onSubmit={(e) => e.preventDefault()}>
                    <TextField
                      required
                      id="outlined-required"
                      label=""
                      value={
                        JSON.parse(formData.missionDisplay.detail).url || ""
                      }
                      onChange={handleChangeJsonField("detail", "url")}
                      onBlur={() => {
                        setTouched({ url: true });
                        validateForm();
                      }}
                      error={touched.url && !!formErrors.url}
                      helperText={touched.url ? formErrors.url : ""}
                    />
                  </form>
                </Box>
                {/* 画像のURL（コメントアウト）
                <Box display="flex" flexDirection="column" alignItems="flex-start" margin="20px auto" paddingBottom="100px" sx={{ maxHeight: 100 }}>
                  <Typography variant="subtitle1" gutterBottom>画像のURL</Typography>
                  <TextField required id="outlined-required" label="" value={JSON.parse(formData.missionDisplay.detail).image || ""} onChange={handleChangeJsonField("detail", "image")} onBlur={() => { setTouched({ image: true }); validateForm(); }} error={touched.image && !!formErrors.image} helperText={touched.image ? formErrors.image : ""} />
                </Box>
                */}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  margin="20px auto"
                >
                  <Typography variant="subtitle1" gutterBottom>
                    詳細
                  </Typography>
                  <TextField
                    type="text"
                    label=""
                    multiline
                    rows={10}
                    value={
                      JSON.parse(formData.missionDisplay.detail).description
                        ? JSON.parse(
                            formData.missionDisplay.detail
                          ).description.join("\n")
                        : ""
                    }
                    onChange={handleChangeJsonField("detail", "description")}
                    sx={{ width: "60%", whiteSpace: "pre-wrap" }}
                    onBlur={() => {
                      setTouched({ detail: true });
                      validateForm();
                    }}
                    error={touched.detail && !!formErrors.detail}
                    helperText={touched.detail ? formErrors.detail : ""}
                  />
                </Box>
              </>
            )}
            {/* WebView */}
            {formData.missionDisplay.missionDisplayTypeId ===
              "019372c6-c935-7000-9eef-4043e704ad85" && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  margin="20px auto"
                  paddingBottom="100px"
                  sx={{ maxHeight: 100 }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    URL
                  </Typography>
                  <TextField
                    required
                    id="outlined-required"
                    label=""
                    value={JSON.parse(formData.missionDisplay.detail).url || ""}
                    onChange={handleChangeJsonField("detail", "url")}
                    onBlur={() => {
                      setTouched({ url: true });
                      validateForm();
                    }}
                    error={touched.url && !!formErrors.url}
                    helperText={touched.url ? formErrors.url : ""}
                  />
                </Box>
              </>
            )}
            <Divider
              sx={{
                width: "680px",
                borderColor: "var(--color-bold-divider)",
                borderBottomWidth: 2,
              }}
            />
            {/* ポイント */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              margin="20px auto"
              paddingBottom="100px"
              sx={{ maxHeight: 100 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                獲得ポイント（pt）
              </Typography>
              <TextField
                required
                id="outlined-required"
                label=""
                type="number"
                value={formData.point}
                onChange={handleChange("point")}
                onBlur={() => {
                  setTouched({ point: true });
                  validateForm();
                }}
                error={touched.point && !!formErrors.point}
                helperText={touched.point ? formErrors.point : ""}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};
